/**
 * Sentry (browser) init. No-op unless VITE_SENTRY_DSN is set, so local dev
 * stays silent and adds zero network traffic. Call initSentry() once, as early
 * as possible in main.tsx (before React mounts).
 */
import * as Sentry from '@sentry/react';

/**
 * Ruído que NÃO é nosso.
 *
 * Um painel de erros só serve enquanto todo item nele for acionável — uma
 * lista cheia de falso positivo treina a gente a ignorar o painel, e aí o
 * bug de verdade passa despercebido. Cada padrão abaixo tem origem
 * identificada fora do nosso código:
 *
 *  • "Object Not Found Matching Id…, MethodName:update" — RPC interno do
 *    add-in do Microsoft Office/Outlook injetado na página. Vira uma
 *    promise rejeitada com um objeto (nem Error é), na aba de quem tem a
 *    extensão instalada. Nada no app dispara ou observa isso.
 *  • ResizeObserver loop… — aviso benigno da própria spec do browser
 *    quando um observer reflow-a no mesmo frame; não quebra nada.
 *  • play() interrupted / AbortError de mídia — o usuário trocou de cena
 *    antes do vídeo começar; já tratamos com catch em todos os call sites.
 *  • Extensões de tradução/antivírus que reescrevem o DOM.
 */
const IGNORE_ERRORS = [
  'Object Not Found Matching Id',
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
  'The play() request was interrupted',
  'The operation was aborted',
  // Extensões e webviews antigos
  'top.GLOBALS',
  'originalCreateNotification',
  'canvas.contentDocument',
  'Non-Error promise rejection captured with value: Object Not Found',
];

/** Stack que nasce numa extensão nunca é bug nosso. */
const DENY_URLS = [
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,
  /^safari-(web-)?extension:\/\//i,
  /^chrome:\/\//i,
  /extensions\//i,
];

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) return; // disabled in dev / when no DSN provided

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      // Mask all text and block media by default to comply with LGPD/GDPR.
      // Use data-sentry-unmask / data-sentry-unblock on elements that are
      // explicitly safe to record.
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    // Performance: sample a fraction of transactions in prod, all in dev.
    tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0,
    // Session Replay: 10% of sessions, 100% of sessions with an error.
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // Only attach tracing headers to our own API.
    tracePropagationTargets: [/^\//, /my360\.site/],
    ignoreErrors: IGNORE_ERRORS,
    denyUrls: DENY_URLS,
    // Última linha: descarta o que passou pelos filtros acima mas tem a
    // assinatura inconfundível de extensão — rejection sem stack trace
    // nenhum, com valor que não é Error. Erro nosso sempre traz frames.
    beforeSend(event, hint) {
      const original = hint?.originalException;
      if (typeof original === 'string' && original.includes('Object Not Found Matching Id')) {
        return null;
      }
      const values = event.exception?.values ?? [];
      const noise = values.some(
        (v) =>
          typeof v.value === 'string' &&
          v.value.includes('Object Not Found Matching Id') &&
          !v.stacktrace?.frames?.length,
      );
      return noise ? null : event;
    },
  });
}

export { Sentry };
